import { ClipboardEvent, ComponentPropsWithoutRef, ReactNode, useState } from 'react';
import { Box } from '@/components/Box/Box';
import { CustomInput, BeforeAfterWrapper, InputSizeVariant, InputWrapper } from './styles';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { InputError } from '../InputError/InputError';
import ConfirmInputTicks from '@/images/ConfirmInputTicks.svg?react';
import { Tooltip } from '@/components/Tooltip/Tooltip';

const COPY_PASTE_HIDE_DELAY = 1000;

export type InputType = {
  label?: ReactNode;
  variant?: InputSizeVariant;
  labelHint?: ReactNode;
  before?: ReactNode;
  after?: ReactNode;
  hasConfirmTicks?: boolean;
  hasErrorComponent?: boolean;
  error?: string;
  apiError?: string;
  children?: ReactNode;
  isCopyPasteDisabled?: boolean;
  overlaySlot?: ReactNode;
  hint?: ReactNode;
} & ComponentPropsWithoutRef<'input'>;

export const Input = ({
  type = 'text',
  label,
  variant = 'medium',
  labelHint,
  before,
  after,
  hasConfirmTicks,
  hasErrorComponent = true,
  error,
  apiError,
  isCopyPasteDisabled,
  disabled,
  overlaySlot,
  hint,
  ...rest
}: InputType) => {
  const hasError = !!error || !!apiError;
  const name = rest.name;
  const [showTooltip, setShowTooltip] = useState(false);

  const onCopyPasteHandler = (event: ClipboardEvent<HTMLInputElement>) => {
    if (isCopyPasteDisabled) {
      event.preventDefault();

      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), COPY_PASTE_HIDE_DELAY);
    }
  };

  return (
    <Box width='100%'>
      <Box display='flex' alignItems='center' justifyContent='space-between' columnGap='10px'>
        {label && (
          <Typography variant='bodySmall' color={COLORS.black} marginBottom='4px'>
            {label}
          </Typography>
        )}
        {labelHint && (
          <Typography variant='caption' color={COLORS.grey[700]} fontWeight='semibold' marginBottom='4px'>
            {labelHint}
          </Typography>
        )}
      </Box>
      <InputWrapper
        variant={variant}
        disabled={disabled}
        hasError={hasError}
        hasAfter={!!after || hasConfirmTicks}
        hasBefore={!!before}
        data-tooltip-id={name}
      >
        {!!before && <BeforeAfterWrapper>{before}</BeforeAfterWrapper>}
        <CustomInput
          variant={variant}
          onPaste={onCopyPasteHandler}
          onCopy={onCopyPasteHandler}
          disabled={disabled}
          type={type}
          hasAfter={!!after || hasConfirmTicks}
          hasBefore={!!before}
          hasOverlaySlot={!!overlaySlot}
          {...rest}
        />
        {overlaySlot}
        {hasConfirmTicks && (
          <Box display='flex' alignItems='center'>
            <ConfirmInputTicks />
          </Box>
        )}
        {!!after && <BeforeAfterWrapper>{after}</BeforeAfterWrapper>}
      </InputWrapper>
      {isCopyPasteDisabled && (
        <Tooltip id={name} isOpen={showTooltip} place='bottom-start' type='hint'>
          Copy-paste disabled for this input.
        </Tooltip>
      )}
      {hasErrorComponent && (
        <Box display='flex'>
          <InputError error={error} apiError={apiError} hint={hint} />
        </Box>
      )}
    </Box>
  );
};
